<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { toast } from 'dashboard/composables/useToast';
import MessageApi from 'dashboard/api/inbox/message';
import EmailVisualEditor from 'dashboard/components/widgets/EmailVisualEditor.vue';
import EmailMarketingTemplatesAPI from 'dashboard/api/emailMarketingTemplates';
import { emitter } from 'shared/helpers/mitt';

// Composer "+Novo email": cria uma thread nova dentro da conversa atual.
// Manda new_thread:true → backend omite In-Reply-To/References → cliente do
// destinatario trata como thread nova no Gmail/Outlook.

const props = defineProps({
  conversationId: { type: Number, required: true },
  defaultTo: { type: String, default: '' },
});

const emit = defineEmits(['close', 'sent']);

const store = useStore();

const to = ref(props.defaultTo || '');
const cc = ref('');
const bcc = ref('');
const subject = ref('');
const body = ref('');
const editorMode = ref('visual'); // 'visual' | 'html'
const sending = ref(false);
const showCcBcc = ref(false);

// Templates (inbox_only|both) — mesmo padrao do ReplyBox.
const templates = ref([]);
const showTemplates = ref(false);
const templateFilter = ref('');

const filteredTemplates = computed(() => {
  if (!templateFilter.value) return templates.value;
  const q = templateFilter.value.toLowerCase();
  return templates.value.filter(
    t =>
      (t.name || '').toLowerCase().includes(q) ||
      (t.category || '').toLowerCase().includes(q)
  );
});

const loadTemplates = async () => {
  try {
    const { data } = await EmailMarketingTemplatesAPI.list({
      context: 'inbox',
    });
    templates.value = data || [];
  } catch {
    templates.value = [];
  }
};

const applyTemplate = t => {
  if (t.subject) subject.value = t.subject;
  body.value = t.body_html;
  showTemplates.value = false;
  toast.success(`Template "${t.name}" aplicado.`);
};

const canSend = computed(
  () =>
    to.value.trim() &&
    subject.value.trim() &&
    body.value &&
    body.value.replace(/<[^>]+>/g, '').trim()
);

const close = () => emit('close');

// Detecta se o body parece HTML estruturado (tem tag de bloco).
// Se sim, manda como email_html_content INDEPENDENTE do toggle Visual/HTML
// — o agente nao deve precisar lembrar de trocar o toggle antes de enviar.
// A extracao do <body> do HTML completo agora eh feita no BACKEND pelo
// Emails::TransactionalRendererService (centralizado pra cobrir composer,
// resposta de thread, API direta, integracoes).
const looksLikeHtml = s =>
  /<(table|tr|td|h[1-6]|p|div|img|a|ul|ol|li|br|hr|span|strong|em|blockquote)\b[^>]*>/i.test(
    s || ''
  );

const send = async () => {
  if (!canSend.value || sending.value) return;
  sending.value = true;

  const savedSubject = subject.value;
  const payload = {
    conversationId: props.conversationId,
    message: body.value,
    private: false,
    subject: savedSubject,
    newThread: true,
    toEmails: to.value,
    ccEmails: cc.value,
    bccEmails: bcc.value,
  };

  // Sempre que parecer HTML, manda como email_html_content. Isso cobre:
  // 1) toggle HTML ativo (intencional)
  // 2) usuario colou HTML no Visual sem perceber o toggle
  if (editorMode.value === 'html' || looksLikeHtml(body.value)) {
    payload.emailHtmlContent = body.value;
  }

  let data;
  try {
    const resp = await MessageApi.create(payload);
    data = resp.data;
  } catch (e) {
    sending.value = false;
    try {
      toast.error(e?.response?.data?.error || 'Erro ao enviar email.');
    } catch (_e) {
      // toast nunca lanca; o try e pelo await acima
    }
    return;
  }

  // ============================================================
  // POST OK — fecha modal IMEDIATAMENTE. TUDO o que vier depois
  // (dispatch store, mitt, alert) eh best-effort. Se algum throw,
  // o modal ja fechou e o usuario nao fica preso.
  // ============================================================
  emit('sent', data);
  emit('close');
  sending.value = false;

  try {
    store.dispatch('addMessage', data);
  } catch (_e) {
    // ignora — UI atualiza via websocket/polling
  }
  try {
    toast.success(`Email enviado: "${savedSubject}"`);
  } catch (_e) {
    // toast nunca lanca; o try e pelo await acima
  }
  const newThreadId =
    data?.content_attributes?.email?.thread_id ||
    data?.contentAttributes?.email?.threadId;
  if (newThreadId) {
    setTimeout(() => {
      try {
        emitter.emit('email-thread:opened', {
          threadId: newThreadId,
          conversationId: props.conversationId,
        });
      } catch (_e) {
        // mitt pode falhar — ignora
      }
    }, 100);
  }
};

onMounted(loadTemplates);

// Strings de UI fora do template pra fugir do vue/no-bare-strings-in-template.
const ui = {
  title: 'Novo email',
  to: 'Para *',
  toPh: 'contato@exemplo.com.br',
  addCcBcc: '+ Cc / Bcc',
  cc: 'Cc',
  bcc: 'Bcc',
  ccPh: 'emails separados por vírgula',
  subject: 'Assunto *',
  subjectPh: 'Assunto do email',
  content: 'Conteúdo *',
  templates: 'Templates',
  noTemplates: 'Nenhum template disponível',
  searchTpl: 'Buscar template...',
  visual: 'Visual',
  html: 'HTML',
  cancel: 'Cancelar',
  send: 'Enviar',
  sending: 'Enviando...',
  htmlPh: '<p>Cole seu HTML aqui...</p>',
  bodyPh: 'Escreva o conteúdo do email.',
};
</script>

<template>
  <div
    class="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4"
    @click.self="close"
  >
    <div
      class="bg-n-background border border-n-weak rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-3xl flex flex-col max-h-[92vh]"
    >
      <div class="flex items-center px-5 py-3 border-b border-n-weak">
        <h2 class="text-sm font-semibold text-n-slate-12">
          <span
            class="i-lucide-mail-plus size-4 inline-block mr-1.5 align-middle"
          />
          {{ ui.title }}
        </h2>
        <button
          type="button"
          class="ml-auto p-1 text-n-slate-10 hover:text-n-slate-12"
          @click="close"
        >
          <span class="i-lucide-x size-4" />
        </button>
      </div>

      <div class="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-3">
        <label class="text-xs text-n-slate-11">
          {{ ui.to }}
          <input
            v-model="to"
            type="text"
            :placeholder="ui.toPh"
            class="reset-base w-full mt-1 text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-background text-n-slate-12"
          />
        </label>

        <button
          v-if="!showCcBcc"
          type="button"
          class="self-start text-xs text-n-brand hover:underline"
          @click="showCcBcc = true"
        >
          {{ ui.addCcBcc }}
        </button>
        <template v-else>
          <label class="text-xs text-n-slate-11">
            {{ ui.cc }}
            <input
              v-model="cc"
              type="text"
              :placeholder="ui.ccPh"
              class="reset-base w-full mt-1 text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-background text-n-slate-12"
            />
          </label>
          <label class="text-xs text-n-slate-11">
            {{ ui.bcc }}
            <input
              v-model="bcc"
              type="text"
              :placeholder="ui.ccPh"
              class="reset-base w-full mt-1 text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-background text-n-slate-12"
            />
          </label>
        </template>

        <label class="text-xs text-n-slate-11">
          {{ ui.subject }}
          <input
            v-model="subject"
            type="text"
            :placeholder="ui.subjectPh"
            class="reset-base w-full mt-1 text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-background text-n-slate-12"
          />
        </label>

        <div class="text-xs text-n-slate-11">
          <div class="flex items-center mb-1">
            <span>{{ ui.content }}</span>
            <button
              type="button"
              class="ml-auto inline-flex items-center gap-1 text-xs text-n-brand hover:underline"
              @click="showTemplates = !showTemplates"
            >
              <span class="i-lucide-layers size-3" />
              {{ ui.templates }}
              <span v-if="templates.length" class="text-[10px] text-n-slate-10">
                ({{ templates.length }})
              </span>
            </button>
            <div
              class="ml-3 inline-flex border border-n-weak rounded-md overflow-hidden"
            >
              <button
                type="button"
                class="px-2 py-0.5 text-[11px]"
                :class="
                  editorMode === 'visual'
                    ? 'bg-n-slate-3 text-n-slate-12'
                    : 'text-n-slate-10 hover:bg-n-slate-2'
                "
                @click="editorMode = 'visual'"
              >
                {{ ui.visual }}
              </button>
              <button
                type="button"
                class="px-2 py-0.5 text-[11px]"
                :class="
                  editorMode === 'html'
                    ? 'bg-n-slate-3 text-n-slate-12'
                    : 'text-n-slate-10 hover:bg-n-slate-2'
                "
                @click="editorMode = 'html'"
              >
                {{ ui.html }}
              </button>
            </div>
          </div>

          <div
            v-if="showTemplates"
            class="mb-2 border border-n-weak rounded-lg max-h-48 overflow-y-auto bg-n-surface-2"
          >
            <div class="p-2 border-b border-n-weak sticky top-0 bg-n-surface-2">
              <input
                v-model="templateFilter"
                type="text"
                :placeholder="ui.searchTpl"
                class="reset-base w-full text-xs border border-n-weak rounded px-2 py-1 bg-n-background text-n-slate-12"
              />
            </div>
            <div
              v-if="!filteredTemplates.length"
              class="p-3 text-center text-xs text-n-slate-10"
            >
              {{ ui.noTemplates }}
            </div>
            <button
              v-for="t in filteredTemplates"
              :key="t.id"
              type="button"
              class="w-full text-left px-3 py-2 hover:bg-n-slate-2 border-b border-n-weak last:border-0"
              @click="applyTemplate(t)"
            >
              <div class="text-xs font-medium text-n-slate-12">
                {{ t.name }}
              </div>
              <div v-if="t.category" class="text-[10px] text-n-slate-10 mt-0.5">
                {{ t.category }}
              </div>
            </button>
          </div>

          <EmailVisualEditor
            v-if="editorMode === 'visual'"
            v-model="body"
            :placeholder="ui.bodyPh"
          />
          <textarea
            v-else
            v-model="body"
            rows="12"
            class="reset-base w-full text-sm font-mono border border-n-weak rounded-lg px-3 py-2 bg-n-background text-n-slate-12"
            :placeholder="ui.htmlPh"
          />
        </div>
      </div>

      <div class="flex items-center gap-2 px-5 py-3 border-t border-n-weak">
        <button
          type="button"
          class="px-3 py-2 text-sm text-n-slate-11 hover:text-n-slate-12"
          @click="close"
        >
          {{ ui.cancel }}
        </button>
        <button
          type="button"
          class="ml-auto px-4 py-2 text-sm font-medium text-white bg-woot-500 rounded-lg hover:bg-woot-600 disabled:opacity-50 inline-flex items-center gap-1.5"
          :disabled="!canSend || sending"
          @click="send"
        >
          <span class="i-lucide-send size-3.5" />
          {{ sending ? ui.sending : ui.send }}
        </button>
      </div>
    </div>
  </div>
</template>
